import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { invoicesAPI, paymentsAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import { format } from 'date-fns';
import { 
  FiArrowLeft, FiDownload, FiSend, FiCopy, 
  FiPlus, FiCheck, FiX, FiEye, FiMessageCircle, FiMail, FiEdit
} from 'react-icons/fi';
import './InvoiceDetail.css';

const InvoiceDetail = () => {
  const { confirm } = useConfirm();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    paymentMethod: 'bank_transfer',
    transactionId: '',
    notes: '',
  });

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getById(id);
      setInvoice(response.data.invoice);
      setPaymentData(prev => ({
        ...prev,
        amount: response.data.invoice.amount_due,
      }));
    } catch (error) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice?.currency || 'AED',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      viewed: 'badge-viewed',
      paid: 'badge-paid',
      partial: 'badge-partial',
      overdue: 'badge-overdue',
      cancelled: 'badge-cancelled',
    };
    return statusClasses[status] || 'badge-draft';
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await invoicesAPI.getPDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleSend = async () => {
    try {
      await invoicesAPI.send(id);
      toast.success('Invoice marked as sent');
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send invoice');
    }
  };

  const handleCancel = async () => {
    const confirmed = await confirm({
      title: 'Cancel Invoice',
      message: 'Are you sure you want to cancel this invoice? This action cannot be undone.',
      type: 'warning',
      confirmText: 'Cancel Invoice',
      cancelText: 'Keep Invoice'
    });
    
    if (!confirmed) return;
    
    try {
      await invoicesAPI.cancel(id);
      toast.success('Invoice cancelled');
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel invoice');
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Invoice',
      message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await invoicesAPI.delete(id);
      toast.success('Invoice deleted');
      window.location.href = '/invoices';
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete invoice');
    }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/invoice/${invoice.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied to clipboard');
  };

  const sendViaWhatsApp = () => {
    const phone = invoice.client_phone;
    if (!phone) {
      toast.error('Client phone number not available');
      return;
    }
    
    const url = `${window.location.origin}/invoice/${invoice.public_token}`;
    const message = `Dear ${invoice.contact_person},\n\nPlease find your invoice ${invoice.invoice_number} for the amount of ${formatCurrency(invoice.total)}.\n\nYou can view and pay your invoice online at:\n${url}\n\nThank you for your business!`;
    
    // Clean phone number (remove spaces, dashes, parentheses)
    const cleanPhone = phone.replace(/[\s()-]/g, '');
    
    // Add country code if not present (default to UAE +971)
    const phoneWithCountryCode = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : 
                                  cleanPhone.startsWith('0') ? '971' + cleanPhone.substring(1) :
                                  cleanPhone.startsWith('971') ? cleanPhone : '971' + cleanPhone;
    
    const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success('Opening WhatsApp...');
  };

  const [sendingEmail, setSendingEmail] = useState(false);

  const sendViaEmail = async () => {
    if (!invoice.client_email) {
      toast.error('Client email address not available');
      return;
    }
    
    try {
      setSendingEmail(true);
      await invoicesAPI.sendEmail(id);
      toast.success('Invoice sent successfully via email');
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await paymentsAPI.create({
        invoiceId: id,
        ...paymentData,
        amount: parseFloat(paymentData.amount),
      });
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Invoice not found</h3>
          <Link to="/invoices" className="btn btn-primary">
            <FiArrowLeft /> Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <Link to="/invoices" className="back-link">
            <FiArrowLeft /> Back to Invoices
          </Link>
          <h1>{invoice.invoice_number}</h1>
          <span className={`badge ${getStatusBadge(invoice.status)}`}>
            {invoice.status}
          </span>
        </div>
        <div className="page-header-actions">
          {invoice.status === 'draft' && (
            <>
              <Link to={`/invoices/${id}/edit`} className="btn btn-primary">
                <FiEdit /> Edit
              </Link>
              <button className="btn btn-success" onClick={handleSend}>
                <FiSend /> Send
              </button>
            </>
          )}
          <button className="btn btn-whatsapp" onClick={sendViaWhatsApp}>
            <FiMessageCircle /> WhatsApp
          </button>
          <button className="btn btn-primary" onClick={sendViaEmail} disabled={sendingEmail}>
            <FiMail /> {sendingEmail ? 'Sending...' : 'Send Email'}
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadPDF}>
            <FiDownload /> Download PDF
          </button>
          <button className="btn btn-secondary" onClick={copyPublicLink}>
            <FiCopy /> Copy Link
          </button>
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <button className="btn btn-danger" onClick={handleCancel}>
              <FiX /> Cancel
            </button>
          )}
        </div>
      </div>

      <div className="invoice-detail-grid">
        <div className="invoice-main">
          {/* Client Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Client</h3>
            </div>
            <div className="card-body">
              <h4>{invoice.company_name}</h4>
              <p>{invoice.contact_person}</p>
              <p className="text-muted">{invoice.client_email}</p>
              {invoice.client_address && (
                <p className="text-muted">{invoice.client_address}</p>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Items</h3>
            </div>
            <div className="card-body">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      {invoice.display_fields?.sku && <th>SKU</th>}
                      {invoice.display_fields?.category && <th>Category</th>}
                      {invoice.display_fields?.item_name !== false && <th>Item Name</th>}
                      {invoice.display_fields?.description !== false && <th style={{ width: '40%' }}>Description</th>}
                      {invoice.display_fields?.unit_price && <th style={{ textAlign: 'right' }}>Unit Price</th>}
                      {invoice.display_fields?.unit && <th style={{ textAlign: 'center' }}>Unit</th>}
                      {invoice.display_fields?.quantity && <th style={{ textAlign: 'center' }}>Qty</th>}
                      {invoice.display_fields?.tax && <th style={{ textAlign: 'right' }}>Tax</th>}
                      <th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={index}>
                        {invoice.display_fields?.sku && <td>{item.sku || '-'}</td>}
                        {invoice.display_fields?.category && <td>{item.category || '-'}</td>}
                        {invoice.display_fields?.item_name !== false && <td className="font-medium">{item.name}</td>}
                        {invoice.display_fields?.description !== false && <td>{item.description || '-'}</td>}
                        {invoice.display_fields?.unit_price && <td style={{ textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>}
                        {invoice.display_fields?.unit && <td style={{ textAlign: 'center' }}>{item.unit || '-'}</td>}
                        {invoice.display_fields?.quantity && <td style={{ textAlign: 'center' }}>{item.quantity}</td>}
                        {invoice.display_fields?.tax && <td style={{ textAlign: 'right' }}>{item.tax_rate || 0}%</td>}
                        <td className="font-medium" style={{ textAlign: 'right' }}>{formatCurrency(Number(item.total || 0) - Number(item.tax_amount || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="invoice-totals">
                <div className="total-row">
                  <span>Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                {invoice.discount_amount > 0 && (
                  <div className="total-row discount">
                    <span>Discount</span>
                    <span>-{formatCurrency(invoice.discount_amount)}</span>
                  </div>
                )}
                <div className="total-row">
                  <span>Tax</span>
                  <span>{formatCurrency(invoice.tax_amount)}</span>
                </div>
                <div className="total-row grand-total">
                  <span>Total</span>
                  <span>{formatCurrency(invoice.total)}</span>
                </div>
                {invoice.amount_paid > 0 && (
                  <>
                    <div className="total-row">
                      <span>Amount Paid</span>
                      <span className="text-success">-{formatCurrency(invoice.amount_paid)}</span>
                    </div>
                    <div className="total-row grand-total">
                      <span>Amount Due</span>
                      <span>{formatCurrency(invoice.amount_due)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Payment Schedule */}
          {invoice.payment_terms && (() => {
            try {
              const paymentTerms = typeof invoice.payment_terms === 'string' 
                ? JSON.parse(invoice.payment_terms) 
                : invoice.payment_terms;
              
              if (paymentTerms.type === 'full' || !paymentTerms.installments) return null;
              
              const installments = paymentTerms.installments || paymentTerms.schedule;
              if (!installments || !Array.isArray(installments)) return null;
              
              return (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Payment Schedule</h3>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>Percentage</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Paid</th>
                            <th style={{ textAlign: 'right' }}>Remaining</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {installments.map((inst, index) => {
                            const totalAmount = invoice.total * (inst.percentage / 100);
                            const amountPaid = parseFloat(inst.amount_paid || 0);
                            const amountRemaining = totalAmount - amountPaid;
                            const isFullyPaid = inst.paid === true;
                            const isPartial = !isFullyPaid && amountPaid > 0;
                            
                            return (
                              <tr key={index} className={isFullyPaid ? 'row-success' : ''}>
                                <td>{index + 1}</td>
                                <td>{inst.description || inst.milestone || `Installment ${index + 1}`}</td>
                                <td>{inst.percentage}%</td>
                                <td style={{ textAlign: 'right' }} className="font-medium">
                                  {formatCurrency(totalAmount)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {amountPaid > 0 ? formatCurrency(amountPaid) : '-'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {isFullyPaid ? '-' : formatCurrency(amountRemaining)}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {isFullyPaid ? (
                                    <span className="badge badge-paid">
                                      <FiCheck /> Paid
                                    </span>
                                  ) : isPartial ? (
                                    <span className="badge badge-partial">
                                      Partial ({formatCurrency(amountRemaining)} left)
                                    </span>
                                  ) : (
                                    <span className="badge badge-warning">Pending</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            } catch (e) {
              return null;
            }
          })()}

          {/* Payments */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Payments</h3>
              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                <button className="btn btn-sm btn-primary" onClick={() => setShowPaymentModal(true)}>
                  <FiPlus /> Record Payment
                </button>
              )}
            </div>
            <div className="card-body">
              {invoice.payments.length === 0 ? (
                <div className="empty-state">
                  <p>No payments recorded</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Amount</th>
                        <th>Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.payment_date)}</td>
                          <td className="capitalize">{payment.payment_method.replace('_', ' ')}</td>
                          <td className="font-medium">{formatCurrency(payment.amount)}</td>
                          <td>{payment.transaction_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="invoice-sidebar">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Details</h3>
            </div>
            <div className="card-body">
              <div className="detail-item">
                <span className="detail-label">Invoice Number</span>
                <span className="detail-value">{invoice.invoice_number}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Issue Date</span>
                <span className="detail-value">{formatDate(invoice.issue_date)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Due Date</span>
                <span className="detail-value">{formatDate(invoice.due_date)}</span>
              </div>
              {invoice.po_number && (
                <div className="detail-item">
                  <span className="detail-label">PO Number</span>
                  <span className="detail-value">{invoice.po_number}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Created By</span>
                <span className="detail-value">{invoice.creator_first_name} {invoice.creator_last_name}</span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Notes</h3>
              </div>
              <div className="card-body">
                <p>{invoice.notes}</p>
              </div>
            </div>
          )}

          {invoice.terms && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Terms & Conditions</h3>
              </div>
              <div className="card-body">
                <p className="text-muted">{invoice.terms}</p>
              </div>
            </div>
          )}

          {/* History */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Activity</h3>
            </div>
            <div className="card-body">
              <div className="activity-list">
                {invoice.history.slice(0, 10).map((item, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      {item.action === 'created' && <FiPlus />}
                      {item.action === 'sent' && <FiSend />}
                      {item.action === 'viewed' && <FiEye />}
                      {item.action === 'paid' && <FiCheck />}
                      {item.action === 'payment_added' && <FiCheck />}
                      {item.action === 'cancelled' && <FiX />}
                    </div>
                    <div className="activity-content">
                      <div className="activity-text">{item.description}</div>
                      <div className="activity-time">{formatDate(item.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Record Payment</h2>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handlePaymentSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label required">Amount</label>
                  <input
                    type="number"
                    name="amount"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    className="form-control"
                    step="0.01"
                    min="0.01"
                    max={invoice.amount_due}
                    required
                  />
                  <span className="form-hint">Maximum: {formatCurrency(invoice.amount_due)}</span>
                </div>

                <div className="form-group">
                  <label className="form-label required">Payment Date</label>
                  <input
                    type="date"
                    name="paymentDate"
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    className="form-control"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="check">Check</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Transaction/Reference ID</label>
                  <input
                    type="text"
                    name="transactionId"
                    value={paymentData.transactionId}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, transactionId: e.target.value }))}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-control"
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;